import { FC, useCallback, useEffect, useMemo } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { Textbox } from "capitalrx-components-2.0/lib/forms";
import { Message, Messages } from "capitalrx-components-2.0/lib/general";
import { Grid, Stack, useResponsive } from "capitalrx-components-2.0/lib/layout";
import { Body, Semibold, Subheading } from "capitalrx-components-2.0/lib/typography";

import { ThemedCheckbox } from "@/App/components";
import { emailValidator, phoneNumberValidator } from "@/App/utils/validation";

const TopBorderedStack = styled(Stack)`
  border-top: solid 1px #cfd9e1;
  padding-top: 16px;
`;

export interface CommunicationPreferencesFormData {
  readonly phoneNumber: string;
  readonly addressLine1: string;
  readonly addressLine2?: string;
  readonly city: string;
  readonly state: string;
  readonly zipCode: string;
  readonly emailAddress: string;
  readonly optIntoMail: boolean;
  readonly optIntoEmail: boolean;
  readonly optIntoSms: boolean;
}

export interface CommunicationPreferencesFormProps {
  readonly addressComplete: boolean;
  readonly emailComplete: boolean;
}

const TRANSLATION_PATH = "MEDICARE_PRESCRIPTION_PAYMENT_PLAN.communicationPreferencesForm";

/**
 * These communication preferences rules are WACK
 *
 * 1. If a member is opted into email but not mail, they may NOT opt out of email
 * 2. If a member is opted into mail but not email, they may NOT opt out of mail
 * 3. Member's can opt in and out of SMS as they choose
 * 4. If the form loads and for some reason the member is opted into neither email nor mail,
 *     default to mail communications being selected
 * 5. If we have an incomplete address/email address, we still allow the member to change their
 *     communication preference, but also display a warning to the member that the info we have is
 *     incomplete.
 */
export const CommunicationPreferencesForm: FC<CommunicationPreferencesFormProps> = ({ addressComplete, emailComplete }) => {
  const { t: translate } = useTranslation();

  const { isCompressed } = useResponsive({
    isCompressed: {
      mobile: true,
      desktop: false,
    },
  });

  const form = useFormContext<CommunicationPreferencesFormData>();

  const optIntoEmail = form.watch("optIntoEmail");
  const optIntoMail = form.watch("optIntoMail");
  const optIntoSms = form.watch("optIntoSms");

  const disableMailCheckbox = useMemo(() => {
    return optIntoMail && !optIntoEmail;
  }, [optIntoEmail, optIntoMail]);

  const disableEmailCheckbox = useMemo(() => {
    return optIntoEmail && !optIntoMail;
  }, [optIntoMail, optIntoEmail]);

  // default to mail if neither email or mail is selected -- this shouldn't really happen but just in case
  useEffect(() => {
    if (!optIntoEmail && !optIntoMail) {
      form.setValue("optIntoMail", true);
    }
  }, []);

  /**
   * Members are required to enter a 10 digit number.
   * We'll sanitize out any dashes, dots, or spaces that they may have entered
   */
  const phoneNumberValidatorHandler = useCallback((value: string) => {
    return phoneNumberValidator(value, translate);
  }, []);

  const emailValidatorHandler = useCallback((value: string) => {
    return emailValidator(value, translate);
  }, []);

  return (
    <TopBorderedStack>
      <Subheading>{translate(`${TRANSLATION_PATH}.changePreferences`)}</Subheading>
      <Semibold>{translate(`${TRANSLATION_PATH}.communicateWithMeVia`)}</Semibold>

      <ThemedCheckbox.Field
        testId="mail"
        readOnly={disableMailCheckbox}
        hideLabel
        name="optIntoMail"
        label={translate(`${TRANSLATION_PATH}.mail`)}
      />
      {optIntoMail && (
        <Grid>
          {!addressComplete && (
            <Grid.Span columns={12}>
              <Message visible appearance={Messages.Error}>
                {translate(`${TRANSLATION_PATH}.incompleteAddress`)}
              </Message>
            </Grid.Span>
          )}
          <Grid.Span columns={12}>
            <Textbox.Field
              name="addressLine1"
              label={translate(`${TRANSLATION_PATH}.streetAddress`)}
              readOnly
            />
          </Grid.Span>
          <Grid.Span columns={12}>
            <Textbox.Field
              name="addressLine2"
              label={translate(`${TRANSLATION_PATH}.addressLine2`)}
              readOnly
            />
          </Grid.Span>
          <Grid.Span columns={isCompressed ? 12 : 4}>
            <Textbox.Field
              name="city"
              label={translate(`${TRANSLATION_PATH}.city`)}
              readOnly
            />
          </Grid.Span>
          <Grid.Span columns={isCompressed ? 12 : 4}>
            <Textbox.Field
              name="state"
              label={translate(`${TRANSLATION_PATH}.state`)}
              readOnly
            />
          </Grid.Span>
          <Grid.Span columns={isCompressed ? 12 : 4}>
            <Textbox.Field
              name="zipCode"
              label={translate(`${TRANSLATION_PATH}.zipCode`)}
              readOnly
            />
          </Grid.Span>
        </Grid>
      )}

      <ThemedCheckbox.Field
        testId="email"
        readOnly={disableEmailCheckbox}
        hideLabel
        name="optIntoEmail"
        label={<>{translate(`${TRANSLATION_PATH}.email`)}<Body inline size="small">*</Body></>}
      />
      {optIntoEmail && (
        <Grid>
          {!emailComplete && (
            <Grid.Span columns={12}>
              <Message appearance={Messages.Error} visible>
                {translate(`${TRANSLATION_PATH}.incompleteEmailAddress`)}
              </Message>
            </Grid.Span>
          )}
          <Grid.Span columns={isCompressed ? 12 : 6}>
            <Textbox.Field
              name="emailAddress"
              label={translate(`${TRANSLATION_PATH}.emailAddress`)}
              validator={emailValidatorHandler}
              testId="email"
              required={optIntoEmail}
              hideLabel
            />
          </Grid.Span>
        </Grid>
      )}

      <ThemedCheckbox.Field
        testId="text-sms"
        hideLabel
        name="optIntoSms"
        label={<>{translate(`${TRANSLATION_PATH}.textSMS`)}<Body inline size="small">*</Body></>}
      />
      {optIntoSms && (
        <Grid>
          <Grid.Span columns={isCompressed ? 12 : 6}>
            <Stack spacing="mini">
              <Body size="mini"><Semibold>{translate(`${TRANSLATION_PATH}.cellPhoneNumber`)}</Semibold></Body>
              <Textbox.Field
                testId="phone-number"
                validator={phoneNumberValidatorHandler}
                name="phoneNumber"
                required={optIntoSms}
                hideLabel
                instructions={translate(`${TRANSLATION_PATH}.tenDigitPhoneInstructions`)}
              />
            </Stack>
          </Grid.Span>
        </Grid>
      )}

      <Body>
        <Body inline size="small">*</Body>
        {translate(`${TRANSLATION_PATH}.formCompletionConsent`)}
      </Body>
    </TopBorderedStack>
  );
};
